import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Sparkles, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

const PAID_STATES = new Set(["deposit_paid", "paid_in_full"]);

export default function StripeSuccess() {
  const { regId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [phase, setPhase] = useState("checking"); // checking | paid | failed
  const [reg, setReg] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      navigate(`/payment/${regId}`);
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;
    const intervalMs = 2000;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/trainings/registrations/${regId}/checkout-status/${sessionId}`);
        // Fetch the latest registration for display
        const { data: r } = await api.get(`/trainings/registrations/${regId}`);
        setReg(r);

        if (data.payment_status === "paid" || PAID_STATES.has(data.registration_status)) {
          setPhase("paid");
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
        if (attempts >= maxAttempts) {
          setPhase("failed");
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        if (attempts >= maxAttempts) {
          setPhase("failed");
          if (pollRef.current) clearInterval(pollRef.current);
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, intervalMs);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [regId, sessionId, navigate]);

  if (phase === "checking") {
    return (
      <div className="pt-32 pb-24 bg-aviana-paper min-h-[60vh]" data-testid="stripe-success-checking">
        <div className="container-aviana max-w-xl text-center">
          <Loader2 size={32} className="animate-spin text-aviana-plum mx-auto mb-6" />
          <h1 className="font-display text-3xl text-aviana-ink">Confirming your payment…</h1>
          <p className="mt-3 text-aviana-muted text-[14px]">Stripe is sealing the deal. This usually takes just a moment.</p>
        </div>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="pt-32 pb-24 bg-aviana-paper min-h-[60vh]" data-testid="stripe-success-failed">
        <div className="container-aviana max-w-xl text-center">
          <ShieldAlert size={32} className="text-aviana-plum mx-auto mb-6" />
          <h1 className="font-display text-3xl text-aviana-ink">We can't confirm your payment yet.</h1>
          <p className="mt-3 text-aviana-muted text-[14px]">Stripe may still be processing, or the session may have been cancelled. Email <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline underline-offset-2">holly@avianayoga.com</a> and we'll sort it out personally — your registration is safely saved either way.</p>
          <Link to={`/payment/${regId}`} className="btn-secondary mt-8 inline-block !py-2.5 !px-6 !text-[11px]">Back to payment options</Link>
        </div>
      </div>
    );
  }

  const first = (reg?.full_name || "there").split(" ")[0];
  const paidLabel = reg?.payment_type === "full" ? "tuition in full" : "deposit";

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="stripe-success-paid">
      <div className="container-aviana max-w-2xl">
        <div className="bg-white border border-aviana-gold/30 rounded-[2rem] p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-aviana-gold/15 text-aviana-gold mx-auto mb-5 flex items-center justify-center">
            <CheckCircle2 size={28} />
          </div>
          <p className="overline-plum mb-3">{reg?.offering_title}</p>
          <h1 className="font-display text-4xl text-aviana-ink leading-tight">Thank you, {first} — your seat is reserved.</h1>
          <p className="mt-4 text-aviana-inkSoft text-[15px] leading-[1.75]">
            Your <strong className="text-aviana-plum">${reg?.amount_today_usd?.toLocaleString()} {paidLabel}</strong> payment has been received via Stripe. A confirmation email is on its way to <strong>{reg?.email}</strong>.
          </p>
          {reg?.balance_due_usd > 0 && (
            <div className="mt-7 bg-aviana-cream/60 rounded-2xl p-5 text-left">
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-aviana-plum mt-0.5" />
                <div>
                  <p className="font-medium text-aviana-ink text-[14px]">Balance: ${reg.balance_due_usd.toLocaleString()}</p>
                  <p className="text-aviana-muted text-[13px] leading-[1.6]">Holly will send a reminder one week before the training starts.</p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/" className="btn-secondary !py-2.5 !px-6 !text-[11px]">Back to home</Link>
            <Link to="/trainings" className="btn-primary !py-2.5 !px-6 !text-[11px]">Explore more offerings</Link>
          </div>
          <p className="mt-8 text-[11px] tracking-[0.18em] uppercase text-aviana-muted flex items-center justify-center gap-2">
            <Sparkles size={11} className="text-aviana-gold" /> With love and light, from Holly
          </p>
        </div>
      </div>
    </div>
  );
}
